import { CAREPLAN_STATUS, CAREPLAN_STATUS_DISPLAY, redirectCareplanPath } from 'op-pages/RO/Careplan/Common';
import { CREATE_CAREPLAN } from 'op-pages/RO/Careplan/Queries';
import { TenantContext } from 'op-contexts';

import { ErrorCircleFill } from 'shared-components/images';
import { GET_PATIENT_CAREPLAN_LIST } from 'op-pages/RO/PatientSummary/PatientProfileQueries';
import { Link, generatePath } from 'react-router-dom';
import moment from 'moment';
import RouteIcon from '@mui/icons-material/Route';
// eslint-disable-next-line no-use-before-define
import React, { Fragment, useState, useMemo, useContext } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import { CareplanStatusBubble } from './CareplanStatusBubble';
import { useOpenCanvasNavOption } from './OpenCanvasNavOption';
import DeleteCareplan from './DeleteCareplan';

import { hasSubmitted, changesFromLastEdited } from 'op-pages/RO/Careplan/Banner/Banner';
import { getRemSize } from 'shared-components/StyledComponents/functions';
import { Box, styled } from '@mui/system';
import { useQuery, useMutation } from '@apollo/client';
import { Delete } from 'shared-components/components';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { Careplan } from 'op-pages/RO/Careplan/Interface';
import { GET_PRACTITIONER } from 'op-graphql/queries';
import { Button, Stack, Tooltip, Typography, useTheme, IconButton, Divider, Menu, MenuItem } from '@mui/material';
import { isDemo } from 'op-utils';
import {
  InsertChart,
  Edit,
  Ballot,
  Forum,
  AutoAwesome,
  MoreHoriz,
  LibraryBooks,
  StackedBarChart,
  Science,
  Add,
  RecordVoiceOver,
} from '@mui/icons-material';
import { UserContext } from 'op-contexts/index';
import {
  GET_ICD10_CATEGORY_LIST,
  GET_PATIENT_DIAGNOSIS_LIST,
  GET_CLINEX_CODES,
} from 'op-pages/RO/Careplan/DiagnosisPages/Queries';
import { PermissionType } from 'op-enums';

const MAX_VISIBLE_OPTIONS = 7;

interface ROPatientDashboardNavType {
  id: string;
}

export interface PatientNavOptionType {
  icon: any;
  label: string;
  linkTo: string;
  id: string;
  disabled: boolean;
  isExternal: boolean;
  isVisible: boolean;
}

const GradientIcon = React.forwardRef<SVGSVGElement>((props, ref) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: 24, height: 24 }}>
      <svg width={0} height={0} style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <linearGradient id="linearColors" x1="5.5" y1="17" x2="22" y2="8.5" gradientUnits="userSpaceOnUse">
            <stop stopColor={theme.palette.primary.main} />
            <stop offset="1" stopColor={theme.palette.secondary.main} />
          </linearGradient>
        </defs>
      </svg>
      <AutoAwesome ref={ref} sx={{ '&& path': { fill: 'url(#linearColors)' } }} {...props} />
    </Box>
  );
});

const menuOptionsWithAi: PatientNavOptionType[] = [
  {
    icon: <InsertChart />,
    label: CurrentAppConfig.PatientSummary.summaryLabel,
    linkTo: '/radiation/patient/:id/summary',
    id: 'summary',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <GradientIcon />,
    label: 'Summary',
    linkTo: '/radiation/patient/:id/ai-summary',
    id: 'ai-summary',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <LinearScaleIcon />,
    label: 'Timeline',
    linkTo: '/radiation/patient/:id/health-timeline',
    id: 'health-timeline',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <Forum />,
    label: 'Ask',
    linkTo: '/radiation/patient/:id/chat',
    id: 'chat',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <RecordVoiceOver sx={{ fill: '#2e2e2e' }} />,
    label: 'Scribe',
    linkTo: '/radiation/patient/:id/scribe',
    id: 'scribe',
    disabled: false,
    isExternal: false,
    isVisible: Boolean(isDemo),
  },
  {
    icon: <RouteIcon />,
    label: 'Pathway',
    linkTo: '/radiation/patient/:id/pathway',
    id: 'pathway',
    disabled: false,
    isExternal: false,
    isVisible: Boolean(isDemo),
  },
  {
    icon: <Ballot />,
    label: 'Treatment Review',
    linkTo: '/radiation/patient/:id/treatment-review',
    id: 'treatment-review',
    disabled: false,
    isExternal: false,
    isVisible: CurrentAppConfig.OTRPage.isVisible,
  },
  {
    icon: <StackedBarChart />,
    label: 'Outcomes',
    linkTo: '/radiation/patient/:id/outcomes',
    id: 'outcomes',
    disabled: false,
    isExternal: false,
    isVisible: CurrentAppConfig.OutcomesPage.isVisible,
  },
  {
    icon: <Science />,
    label: 'Labs',
    linkTo: '/radiation/patient/:id/labs',
    id: 'labs',
    disabled: false,
    isExternal: false,
    isVisible: CurrentAppConfig.PatientSummary.labsEnabled,
  },
  {
    icon: <LibraryBooks />,
    label: 'Documents',
    linkTo: '/radiation/patient/:id/documents/view',
    id: 'documents',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <Edit />,
    label: 'Notes',
    linkTo: '/radiation/patient/:id/notes',
    id: 'notes',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
];

const menuOptions: PatientNavOptionType[] = [
  {
    icon: <InsertChart />,
    label: CurrentAppConfig.PatientSummary.summaryLabel,
    linkTo: '/radiation/patient/:id/summary',
    id: 'summary',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <LibraryBooks />,
    label: 'Documents',
    linkTo: '/radiation/patient/:id/documents/view',
    id: 'documents',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <Edit />,
    label: 'Notes',
    linkTo: '/radiation/patient/:id/notes',
    id: 'notes',
    disabled: false,
    isExternal: false,
    isVisible: true,
  },
  {
    icon: <Science />,
    label: 'Labs',
    linkTo: '/radiation/patient/:id/labs',
    id: 'labs',
    disabled: false,
    isExternal: false,
    isVisible: CurrentAppConfig.PatientSummary.labsEnabled,
  },
  {
    icon: <RouteIcon />,
    label: 'Pathway',
    linkTo: '/radiation/patient/:id/pathway',
    id: 'pathway',
    disabled: false,
    isExternal: false,
    isVisible: Boolean(isDemo),
  },
  {
    icon: <Ballot />,
    label: 'Treatment Review',
    linkTo: '/radiation/patient/:id/treatment-review',
    id: 'treatment-review',
    disabled: false,
    isExternal: false,
    isVisible: CurrentAppConfig.OTRPage.isVisible,
  },
  {
    icon: <StackedBarChart />,
    label: 'Outcomes',
    linkTo: '/radiation/patient/:id/outcomes',
    id: 'outcomes',
    disabled: false,
    isExternal: false,
    isVisible: CurrentAppConfig.OutcomesPage.isVisible,
  },
];

const StyledCareplanName = styled('p')`
  font-style: normal;
  font-weight: normal;
  font-size: ${getRemSize(14)};
  line-height: 1.25em;
  text-transform: uppercase;
  color: ${(props) => props.theme.palette.text.primary};
  min-width: 100%;
`;

const StyledDiagnosisCode = styled('span')`
  font-style: normal;
  font-weight: bold;
  font-size: ${getRemSize(14)};
  line-height: 1.25em;
  color: ${(props) => props.theme.palette.text.primary};
`;

const StyledDiagnosisTitleDiv = styled('div')`
  margin: 0;
`;

const StyledDiagnosisCodeDescription = styled('span')`
  font-style: normal;
  font-weight: normal;
  font-size: ${getRemSize(14)};
  line-height: 1.25em;
  color: ${(props) => props.theme.palette.text.primary};
`;

const StyledPatientNavContainer = styled(Stack)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 11px;

  &:hover {
    background: ${(props) => props.theme.palette.action.hover};
    border-radius: 4px;

    a {
      text-decoration: none;
    }
  }

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const CareplanDetailWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const StyledScrollableRow = styled(Stack, {
  // Prevents the $collapsed prop from being passed to the DOM element.
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed?: boolean }>`
  overflow-y: auto;
  min-width: 100%;
  align-self: stretch;
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
  margin-top: 16px;

  /* Add bottom padding to ensure last item is fully visible when scrolled */
  padding-bottom: 8px;

  /* Ensure children don't get cut off */
  > * {
    flex-shrink: 0;
  }
`;

const StyledCareplanListItem = styled('div', {
  // Prevents the $collapsed prop from being passed to the DOM element.
  shouldForwardProp: (prop) => prop !== '$collapsed',
})<{ $collapsed?: boolean }>`
  padding: 0 4px;
  cursor: pointer;
`;

const StyledNavLinksContainer = styled(Stack)`
  padding-top: 16px;
  width: 100%;
`;

const AnimatedText = styled(Typography)`
  opacity: 1;
  visibility: visible;

  overflow: hidden;
  white-space: nowrap;
  width: 100%;
`;

const UnsubmittedTooltip = styled('div')`
  color: white;
  display: flex;
  align-items: center;
  svg {
    width: 20px;
    height: 20px;
    margin-right: 6px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  width: 220px;
  padding: 0;
  && {
    .ro-side-patient-nav-item-row {
      width: 100%;
      height: 40px;
      align-items: center;
      border-left: 0px !important;
      border-radius: 0px !important;
      padding: 0px 16px;
      background-color: ${(props) => (props.selected ? props.theme.palette.action.selected : 'transparent')} !important;
      .ro-side-patient-nav-item {
        display: flex !important;
        gap: 8px;
        width: 100%;
        height: 100%;
        text-decoration: none;
        align-items: center;
        svg {
          width: 24px;
          height: 24px;
        }
        border-left: 0px !important;
      }
    }
  }
`;

const PatientNavLinks = (props: any): JSX.Element | null => {
  const { linkTo, icon, label, id, disabled, collapsed } = props.option;
  const location = useLocation();
  const match = useRouteMatch<ROPatientDashboardNavType>();
  const splitPath = location.pathname.split('/');
  const isActive = splitPath.includes(id.toLowerCase());
  const { id: patientId } = match.params;
  const url = generatePath(linkTo, { id: patientId });
  const active = isActive ? 'active' : '';
  return (
    <NavLinks url={url} active={active} label={label} disabled={disabled} icon={icon} id={id} collapsed={collapsed} />
  );
};

const ExternalNavLinks = (props: any): JSX.Element | null => {
  const { linkTo, icon, label, disabled, id, collapsed } = props.option;
  return (
    <NavLinks
      id={id}
      url={{ pathname: linkTo }}
      icon={icon}
      label={label}
      disabled={disabled}
      active=""
      collapsed={collapsed}
      additionalProps={{ target: '_blank' }}
    />
  );
};

const NavLinks = (props: any): JSX.Element | null => {
  const { url, active, label, disabled, icon, additionalProps, id, collapsed } = props;
  const theme = useTheme();

  const dataTestId = `ro-nav-link-${id}`;
  const linkClass = 'ro-side-patient-nav-item-row';

  const Label = (): JSX.Element => (
    <span
      className="ro-side-patient-nav-item-title"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        transition: collapsed ? 'all 0.5s ease-in-out' : 'all 0.2s ease-in-out',
        opacity: collapsed ? 0.5 : 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}>
      <AnimatedText variant="body1">{label}</AnimatedText>
    </span>
  );

  return (
    <Box
      data-test-id={dataTestId}
      className={linkClass}
      sx={{
        backgroundColor: active ? theme.palette.secondary.light : 'transparent',
        borderLeft: '4px solid',
        borderColor: active ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: active ? theme.palette.secondary.light : theme.palette.action.hover,
        },
      }}>
      {disabled ? (
        <div className="ro-side-patient-nav-item">
          <Tooltip
            title={collapsed ? label : ''}
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  color: 'white',
                  lineHeight: '1.4rem',
                  fontSize: '0.9rem',
                  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                  backgroundColor: theme.palette.primary.light,
                },
              },
            }}>
            <span>{icon}</span>
          </Tooltip>
          <Label />
        </div>
      ) : (
        <Link to={url} className="ro-side-patient-nav-item" {...additionalProps}>
          <Tooltip
            title={collapsed ? label : ''}
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  color: 'white',
                  lineHeight: '1.4rem',
                  fontSize: '0.9rem',
                  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                  backgroundColor: theme.palette.primary.light,
                },
              },
            }}>
            <span>{icon}</span>
          </Tooltip>
          <Label />
        </Link>
      )}
    </Box>
  );
};

interface CareplanNavLinkProps {
  careplan: Careplan;
  patientId: string;
  key: string;
  index: number;
  collapsed?: boolean;
}

const getCareplanTooltipText = (careplanStatus: string): string => {
  return CAREPLAN_STATUS_DISPLAY[careplanStatus as keyof typeof CAREPLAN_STATUS_DISPLAY];
};

const CareplanNavLinks = ({ careplan, patientId, index, collapsed }: CareplanNavLinkProps): JSX.Element => {
  const [careplanHovered, setCareplanHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { id, careplanStatus, diagnosis, careplanName } = careplan;
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const theme = useTheme();
  const tenantContext = useContext(TenantContext);

  const { data: pracData } = useQuery(GET_PRACTITIONER, {
    variables: { patientId },
    skip: !patientId,
  });

  const practitionerTimezone = useMemo(
    () => pracData?.practitioner?.timezone || CurrentAppConfig.DefaultTimezone,
    [pracData?.practitioner?.timezone],
  );
  const careplanPath = redirectCareplanPath(careplan, patientId);
  const hasUnsubmittedChanges = hasSubmitted(careplan, practitionerTimezone) && changesFromLastEdited(careplan);
  const handleClickDelete = (event: any) => {
    const { currentTarget } = event;
    setAnchorEl(currentTarget);
    setIsPopUpOpen(!isPopUpOpen);
  };

  const handleClosePopUp = () => {
    setIsPopUpOpen(false);
    setCareplanHovered(false);
  };
  const voidTimeRestriction = tenantContext.voidCareplanExpiryPeriod;

  const submittedAtDate = moment(careplan?.submittedAt);
  const timeDifference = moment.duration(moment().diff(submittedAtDate)).asHours();

  const careplanCanBeVoidedOrDeleted = careplan?.submittedAt ? timeDifference < voidTimeRestriction : true;

  // If collapsed, just return a status bubble with tooltip
  if (collapsed) {
    return (
      <Tooltip
        componentsProps={{
          tooltip: {
            sx: {
              color: 'white',
              lineHeight: '1.4rem',
              minWidth: '300px',
              fontSize: '0.9rem',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
              backgroundColor: theme.palette.primary.light,
            },
          },
        }}
        placement="right"
        enterDelay={700}
        enterNextDelay={700}
        title={
          careplanStatus &&
          careplanStatus !== CAREPLAN_STATUS.voided && (
            <>
              {careplanName
                ? `${getCareplanTooltipText(careplanStatus)}: ${careplanName}`
                : `${getCareplanTooltipText(careplanStatus)}: ${diagnosis?.diagnosisCode?.diagnosisCode || ''}`}
              {hasUnsubmittedChanges && (
                <UnsubmittedTooltip>
                  <ErrorCircleFill /> Unsubmitted changes
                </UnsubmittedTooltip>
              )}
            </>
          )
        }>
        <StyledCareplanListItem key={id} data-test-id={`careplan-nav-link-${index}`} id={`template-${id}`}>
          <Link
            to={careplanPath}
            data-testid={`careplan-id-${id}`}
            style={{ textDecoration: 'none', display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
            <CareplanStatusBubble status={careplanStatus} hasUnsubmittedChanges={hasUnsubmittedChanges} />
          </Link>
        </StyledCareplanListItem>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip
        componentsProps={{
          tooltip: {
            sx: {
              color: 'white',
              lineHeight: '1.4rem',
              minWidth: '300px',
              fontSize: '0.9rem',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
              backgroundColor: theme.palette.primary.light,
            },
          },
        }}
        placement="right"
        enterDelay={700}
        enterNextDelay={700}
        title={
          careplanStatus &&
          careplanStatus !== CAREPLAN_STATUS.voided && (
            <>
              {careplanName
                ? `${getCareplanTooltipText(careplanStatus)}: ${careplanName}`
                : `${getCareplanTooltipText(careplanStatus)}: ${diagnosis?.diagnosisCode?.diagnosisCode || ''}`}
              {hasUnsubmittedChanges && (
                <UnsubmittedTooltip>
                  <ErrorCircleFill /> Unsubmitted changes
                </UnsubmittedTooltip>
              )}
            </>
          )
        }>
        <StyledCareplanListItem
          onMouseEnter={() => setCareplanHovered(true)}
          onMouseLeave={() => setCareplanHovered(false)}
          key={id}
          data-test-id={`careplan-nav-link-${index}`}
          id={`template-${id}`}>
          <StyledPatientNavContainer marginTop={1} style={{ display: 'flex', flexDirection: 'row' }}>
            <CareplanDetailWrapper>
              <CareplanStatusBubble status={careplanStatus} hasUnsubmittedChanges={hasUnsubmittedChanges} />
              <Link
                to={careplanPath}
                data-testid={`careplan-id-${id}`}
                style={{ textDecoration: 'none', paddingLeft: '18px' }}>
                {careplanName ? (
                  <StyledCareplanName> {careplanName} </StyledCareplanName>
                ) : (
                  <StyledDiagnosisTitleDiv>
                    <StyledDiagnosisCode> {diagnosis.diagnosisCode?.diagnosisCode} </StyledDiagnosisCode>
                    <StyledDiagnosisCodeDescription>
                      {diagnosis.diagnosisCode?.diagnosisDescription || 'Draft careplan'}
                    </StyledDiagnosisCodeDescription>
                  </StyledDiagnosisTitleDiv>
                )}
              </Link>
            </CareplanDetailWrapper>
            {careplan.careplanStatus !== CAREPLAN_STATUS.cpot &&
              careplan.careplanStatus !== CAREPLAN_STATUS.dxSubmitted &&
              careplanCanBeVoidedOrDeleted && (
                <div onClick={handleClickDelete}>
                  <Delete id={`dd-delete-${careplan.id}`} show={careplanHovered || isPopUpOpen} />
                </div>
              )}
          </StyledPatientNavContainer>
        </StyledCareplanListItem>
      </Tooltip>
      <DeleteCareplan
        patientId={patientId}
        careplan={careplan}
        handlePopUpClose={handleClosePopUp}
        anchorEl={anchorEl}
        isPopUpOpen={isPopUpOpen}
      />
    </>
  );
};

interface ROSidePatientNavProps {
  patientId?: string;
  refreshChartSummary?: any;
  createManagementPlan?: any;
  createReferralExistingService?: any;
  disableCreateButton?: boolean;
  showButtons?: boolean;
  className?: string;
  $collapsed?: boolean;
}

const ROSidePatientNav = (props: ROSidePatientNavProps): JSX.Element => {
  const { $collapsed } = props;
  const match = useRouteMatch<ROPatientDashboardNavType>();
  const { id: patientId } = match.params;
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const userContext = useContext(UserContext);
  const { hasFeature, hasPermission } = userContext;
  const hasAIFeatures = hasFeature('AI Features');
  const canCreateCareplan = hasPermission(PermissionType.CAREPLAN_CREATE);
  const canViewPatientSummaryCareplans = hasPermission(PermissionType.PATIENT_SUMMARY_CAREPLANS_VIEW);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const activeOptionId = location.pathname.split('/').pop();
  const isSimpleDiagnosisSelectionEnabled = hasFeature('SimpleDiagnosisSelection');
  const tenantContext = useContext(TenantContext);
  const showTreatmentReview = tenantContext?.showTreatmentReview || false;
  // Preload queries on hover
  useQuery(GET_ICD10_CATEGORY_LIST, {
    variables: { isPrimaryDiagnosis: true },
    skip: !isHovered,
    fetchPolicy: 'cache-first',
  });

  useQuery(GET_PATIENT_DIAGNOSIS_LIST, {
    variables: { patientId, diagnosisType: 'primary' },
    skip: !isHovered || !patientId,
    fetchPolicy: 'cache-first',
  });

  useQuery(GET_CLINEX_CODES, {
    variables: { patientId },
    skip: !isHovered || !patientId,
    fetchPolicy: 'cache-first',
  });
  const [createCareplan] = useMutation(CREATE_CAREPLAN);
  const createCareplanClick = (): void => {
    if (!isSimpleDiagnosisSelectionEnabled) {
      history.push(`/radiation/patient/${patientId}/careplan/create`);
    } else {
      createCareplan({ variables: { patientId } }).then(({ data }) => {
        const { careplan } = data.createCareplan;
        history.push(`/radiation/patient/${patientId}/careplan/${careplan.id}`);
      });
    }
  };

  const { data: careplanData } = useQuery(GET_PATIENT_CAREPLAN_LIST, {
    variables: { patientId },
    fetchPolicy: 'cache-and-network',
    skip: !canViewPatientSummaryCareplans,
  });

  const openCanvasOption = useOpenCanvasNavOption(patientId);
  const filteredMenuOptions = useMemo(() => {
    const options = hasAIFeatures ? menuOptionsWithAi : menuOptions;
    if (!openCanvasOption) return options;
    // Directly under the Patient Chart entry.
    const summaryIndex = options.findIndex((option) => option.id === 'summary');
    const insertAt = summaryIndex >= 0 ? summaryIndex + 1 : 0;
    return [...options.slice(0, insertAt), openCanvasOption, ...options.slice(insertAt)];
  }, [hasAIFeatures, openCanvasOption]);

  // enable showTreatmentReview if it is true
  const filteredMenuWithTenantConfigOptions = useMemo(
    () =>
      filteredMenuOptions.map((menu) => {
        if (menu.id === 'treatment-review') {
          return { ...menu, isVisible: showTreatmentReview };
        }
        return menu;
      }),
    [filteredMenuOptions, showTreatmentReview],
  );
  const visibleMenuOptions = useMemo(() => {
    // Top MAX_VISIBLE_OPTIONS visible options are shown and rest of visible options are hidden in the collapsed state
    return filteredMenuWithTenantConfigOptions
      .filter((option: PatientNavOptionType) => option.isVisible)
      .slice(0, MAX_VISIBLE_OPTIONS);
  }, [filteredMenuWithTenantConfigOptions]);

  const extendedMenuOptions = useMemo(() => {
    return filteredMenuWithTenantConfigOptions.filter((option) => option.isVisible).slice(MAX_VISIBLE_OPTIONS);
  }, [filteredMenuWithTenantConfigOptions]);

  const extendedMenuSelected = extendedMenuOptions.find((option) => activeOptionId === option.id.toLowerCase());
  const moreTooltipTitle = $collapsed ? extendedMenuOptions?.map((option) => option.label).join(', ') : '';
  return (
    <Fragment>
      <Stack sx={{ alignItems: 'center', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <StyledNavLinksContainer>
          <Stack className="ro-side-patient-nav" marginBottom={1}>
            {visibleMenuOptions.map((option) =>
              option.isExternal ? (
                <ExternalNavLinks option={{ ...option, collapsed: $collapsed }} key={option.label} />
              ) : (
                <PatientNavLinks option={{ ...option, collapsed: $collapsed }} key={option.label} />
              ),
            )}
          </Stack>
          {extendedMenuOptions.length > 0 && (
            <>
              <Tooltip
                title={moreTooltipTitle}
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      color: 'white',
                      lineHeight: '1.4rem',
                      fontSize: '0.9rem',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      backgroundColor: theme.palette.primary.light,
                    },
                  },
                }}>
                <IconButton
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  data-testid="ro-side-patient-nav-more-button"
                  sx={{
                    width: '100%',
                    height: '40px',
                    padding: '16px 16px 16px 16px 11px',
                    borderLeft: extendedMenuSelected ? '4px solid' : 'none',
                    borderColor: extendedMenuSelected ? 'primary' : 'transparent',
                    borderRadius: '4px',
                    backgroundColor: extendedMenuSelected ? 'secondary.light' : 'transparent',
                  }}>
                  <MoreHoriz />
                </IconButton>
              </Tooltip>
              <Menu
                open={isMenuOpen}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}>
                {extendedMenuOptions.map((option) => (
                  <StyledMenuItem key={option.label} selected={activeOptionId === option.id.toLowerCase()}>
                    {option.isExternal ? (
                      <ExternalNavLinks option={{ ...option }} key={option.label} />
                    ) : (
                      <PatientNavLinks option={{ ...option }} key={option.label} />
                    )}
                  </StyledMenuItem>
                ))}
              </Menu>
            </>
          )}
        </StyledNavLinksContainer>
        {canViewPatientSummaryCareplans && !$collapsed && (
          <Stack
            width={1}
            paddingLeft={1}
            marginBottom={2}
            sx={{
              borderWidth: '1px 0 1px 0',
              borderStyle: 'solid',
              borderColor: theme.palette.grey[300],
              backgroundColor: theme.palette.action.hover,
            }}>
            <Typography paddingLeft={1} lineHeight={'28px'} variant="caption">
              Careplans
            </Typography>
          </Stack>
        )}
        {$collapsed && (canCreateCareplan || canViewPatientSummaryCareplans) && (
          <Divider sx={{ width: '100%', marginBottom: '10px' }} />
        )}
        {canCreateCareplan && (
          <Tooltip
            title={$collapsed ? 'Create careplan' : ''}
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  color: 'white',
                  lineHeight: '1.4rem',
                  fontSize: '0.9rem',
                  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                  backgroundColor: theme.palette.primary.light,
                },
              },
            }}>
            <Button
              id="pts-create-careplan-button"
              data-testid="pts-create-careplan-button"
              variant="contained"
              size={$collapsed ? 'small' : 'large'}
              sx={{
                color: theme.palette.primary.contrastText,
                minWidth: $collapsed ? '34px' : 'auto',
                width: $collapsed ? '35px' : 0.9,
                transition: $collapsed ? 'all 0.5s ease-in-out' : 'all 0.2s ease-in-out',
              }}
              onMouseEnter={() => setIsHovered(true)}
              onClick={createCareplanClick}>
              <Add sx={{ color: theme.palette.primary.contrastText }} />
              <Typography
                variant="subtitle2"
                color="primary.contrastText"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: $collapsed ? 'all 0.5s ease-in-out' : 'all 0.2s ease-in-out',
                  opacity: $collapsed ? 0 : 1,
                  paddingLeft: $collapsed ? '0px' : '8px',
                }}>
                Create Careplan
              </Typography>
            </Button>
          </Tooltip>
        )}
        {canViewPatientSummaryCareplans && (
          <>
            {$collapsed && <Divider sx={{ width: '100%', marginTop: '10px' }} />}
            <StyledScrollableRow>
              {careplanData &&
                careplanData.careplanList
                  .filter((careplan: Careplan) => careplan.careplanStatus !== CAREPLAN_STATUS.voided)
                  .map(
                    (careplan: Careplan, index: number): JSX.Element => (
                      <CareplanNavLinks
                        careplan={careplan}
                        patientId={patientId}
                        key={careplan.id}
                        index={index}
                        collapsed={$collapsed}
                      />
                    ),
                  )}
            </StyledScrollableRow>
          </>
        )}
      </Stack>
    </Fragment>
  );
};

export default ROSidePatientNav;
